import type {
  OptionsOrGroups,
  GroupBase,
  MenuPlacement,
  ActionMeta,
  OnChangeValue,
  SelectInstance
} from "react-select";
import type { FieldStatus } from "../types";
import type { ForwardedRef } from "react";

export enum SelectSizes {
  DEFAULT = "Default",
  SMALL = "Small"
}

export type SelectStyleConfig = {
  control?: {
    boxShadow?: string;
    border?: string;
    borderRadius?: string;
    backgroundColor?: string;
    focusedBorder?: string;
  };
};

export type SelectFooterButtonProps =
  | {
      footerButtonText?: never;
      onClickFooterButton?: never;
      shouldCloseOnFooterButtonClick?: never;
    }
  | {
      footerButtonText: string;
      onClickFooterButton: () => void;
      shouldCloseOnFooterButtonClick: boolean;
    };

export type SelectProps<T extends boolean = false> = {
  name?: string;
  id?: string;
  label: string;
  "aria-describedby"?: string;
  /**
   * Use memoized options to prevent unnecessary re-renders
   */
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
  filterOption?: (option: SelectOption, inputValue: string) => boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  onSearch?: (inputValue: string) => void;
  closeMenuOnSelect?: boolean;
  blurInputOnSelect?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  placeholder?: string;
  // Null value is used to hide menu when options are empty
  noOptionsMessage?: string | null;
  maxMenuHeight?: number;
  /**
   * Use memoized leftIcon element to prevent unnecessary re-renders
   */
  leftIcon?: () => React.ReactElement;
  width?: string;
  minMenuWidth?: number | string;
  openMenuOnFocus?: boolean;
  status?: FieldStatus;
  placement?: MenuPlacement;
  autoFocus?: boolean;
  isLoading?: boolean;
  size?: SelectSizes;
  styleConfig?: SelectStyleConfig;
  menuPortalTarget?: HTMLElement | null;
  onCreateOption?: (inputValue: string) => void;
  ref?: ForwardedRef<SelectRef<T>> | null;
  handleScrolledToBottom?: () => void;
  footerButtonText?: string;
  onClickFooterButton?: () => void;
  shouldCloseOnFooterButtonClick?: boolean;
  menuIsOpen?: boolean;
  menuShouldBlockScroll?: boolean;
} & SelectValueProps<T> &
  SelectVirtualizedProps;

export type SelectOnChange<T extends boolean = false> = (
  value: T extends true ? string[] : string,
  actionMeta: ActionMeta<SelectOption>,
  orignalOption: OnChangeValue<SelectOption, T>
) => void;

type SelectValueProps<T extends boolean> = {
  isMulti?: T;
  value?: T extends true ? string[] : string | null;
  defaultValue?: T extends true ? string[] : string | null;
  onChange?: SelectOnChange<T>;
  onBlur?: (evt: React.FocusEvent<HTMLInputElement>) => void;
};

type SelectVirtualizedProps = Partial<{
  isVirtualized: boolean;
  menuWidth: number;
  menuHeight: number;
}>;

export type SelectOption = {
  value: string;
  label: string | Omit<React.ReactNode, "null">;
  icon?: React.ReactElement;
} & Record<string, any>;

export type SelectRef<IsMulti extends boolean> = SelectInstance<
  SelectOption,
  IsMulti,
  GroupBase<SelectOption>
>;

export type GroupBaseOptions = GroupBase<SelectOption>;
